import {
  AtomicBlockUtils,
  ContentState,
  EditorState,
  Modifier,
  SelectionState,
  convertFromRaw,
  convertToRaw,
  genKey,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

export const richTextEditorToolbar = {
  options: [
    'fontFamily',
    'fontSize',
    'inline',
    'blockType',
    'list',
    'textAlign',
    'colorPicker',
    'link',
    'image',
    'remove',
    'history',
  ],
  inline: {
    inDropdown: false,
    options: ['bold', 'italic', 'underline', 'strikethrough'],
  },
  fontFamily: {
    // Broad set: Microsoft Office / Windows families + common web-safe and
    // Google fonts, so text copied from other sites/docs keeps a matching font
    // in the dropdown instead of falling back. Alphabetical.
    options: [
      'Arial',
      'Arial Black',
      'Bahnschrift',
      'Book Antiqua',
      'Bookman Old Style',
      'Calibri',
      'Cambria',
      'Candara',
      'Century Gothic',
      'Comic Sans MS',
      'Consolas',
      'Constantia',
      'Corbel',
      'Courier New',
      'Ebrima',
      'Franklin Gothic',
      'Gabriola',
      'Garamond',
      'Georgia',
      'Gill Sans',
      'Helvetica',
      'Impact',
      'Ink Free',
      'Lato',
      'Lucida Console',
      'Lucida Sans Unicode',
      'Montserrat',
      'MS Sans Serif',
      'Open Sans',
      'Palatino Linotype',
      'Poppins',
      'Roboto',
      'Rockwell',
      'Segoe Print',
      'Segoe UI',
      'Sitka',
      'Tahoma',
      'Times New Roman',
      'Trebuchet MS',
      'Verdana',
    ],
  },
  blockType: { inDropdown: true },
  list: { inDropdown: false },
  textAlign: { inDropdown: false },
  link: { visible: false },
  history: { inDropdown: false },
};

// ---------------------------------------------------------------------------
// Font fallback stacks.
//
// The dropdown above is a Microsoft Office / Windows font list, and both
// draftjs-utils (which renders `fontfamily-X` as the bare `font-family: X`)
// and draftjs-to-html emit that single family with NO fallbacks. On a Windows
// browser every one of them resolves, so picking a font visibly changes the
// text. On iOS and Android almost NONE of them exist - Calibri, Cambria,
// Candara, Corbel, Segoe UI, Bahnschrift, Ebrima, Sitka... are all Windows-only
// - so the WebView silently falls back to its default face and the text looks
// completely unchanged. That is the "font applies but the mobile UI doesn't
// reflect it" bug: the style IS applied, it just has nothing to resolve to.
// (The reverse direction worked because a font set on mobile was being LOOKED
// AT on Windows, where the family does exist.)
//
// Each family therefore maps to a stack: the original first (so Windows/web is
// bit-identical to before), then the closest face that actually ships on iOS,
// then the closest Android/Noto face or one of Android's family aliases
// (sans-serif-condensed, casual, cursive, monospace...), then a generic. A pick
// now changes how the text looks on every platform, and picks of a different
// CATEGORY (serif vs sans vs mono vs handwriting) stay visually distinct on
// mobile even where the exact face is unavailable.
//
// This is a RENDERING concern only: the draft model and the sanitizer keep
// storing the canonical single family name, so the dropdown keeps highlighting
// the right entry and html round-trips unchanged (sanitizeStyle collapses a
// stack back to its first recognised family on load).
export const FONT_FAMILY_STACKS = {
  'Arial': 'Arial, Helvetica, "Helvetica Neue", sans-serif',
  'Arial Black': '"Arial Black", Gadget, "Helvetica Neue", sans-serif-black, sans-serif',
  'Bahnschrift': 'Bahnschrift, "DIN Alternate", "DIN Condensed", "Roboto Condensed", sans-serif-condensed, sans-serif',
  'Book Antiqua': '"Book Antiqua", Palatino, "Palatino Linotype", "Hoefler Text", serif',
  'Bookman Old Style': '"Bookman Old Style", Bookman, Georgia, "Noto Serif", serif',
  'Calibri': 'Calibri, "Segoe UI", Candara, Avenir, "Avenir Next", Roboto, sans-serif',
  'Cambria': 'Cambria, Charter, "Iowan Old Style", "Noto Serif", Georgia, serif',
  'Candara': 'Candara, Optima, "Gill Sans", "Noto Sans", sans-serif',
  'Century Gothic': '"Century Gothic", Futura, "Avenir Next", sans-serif',
  'Comic Sans MS': '"Comic Sans MS", "Chalkboard SE", "Marker Felt", casual, cursive',
  'Consolas': 'Consolas, Menlo, "Courier New", monospace',
  'Constantia': 'Constantia, "Iowan Old Style", Charter, "Noto Serif", Georgia, serif',
  'Corbel': 'Corbel, Avenir, "Segoe UI", Roboto, sans-serif',
  'Courier New': '"Courier New", Courier, Menlo, monospace',
  'Ebrima': 'Ebrima, "Segoe UI", Roboto, sans-serif',
  'Franklin Gothic': '"Franklin Gothic", "Franklin Gothic Medium", "Helvetica Neue", "Roboto Condensed", sans-serif-condensed, sans-serif',
  'Gabriola': 'Gabriola, "Snell Roundhand", Zapfino, "Dancing Script", cursive',
  'Garamond': 'Garamond, Baskerville, "Hoefler Text", "Noto Serif", serif',
  'Georgia': 'Georgia, "Noto Serif", "Times New Roman", serif',
  'Gill Sans': '"Gill Sans", "Gill Sans MT", Optima, "Noto Sans", sans-serif',
  'Helvetica': 'Helvetica, "Helvetica Neue", Arial, sans-serif',
  'Impact': 'Impact, Haettenschweiler, Charcoal, "Arial Black", sans-serif-condensed, sans-serif',
  'Ink Free': '"Ink Free", "Bradley Hand", "Marker Felt", casual, cursive',
  'Lato': 'Lato, "Helvetica Neue", Roboto, sans-serif',
  'Lucida Console': '"Lucida Console", "Lucida Sans Typewriter", Monaco, Menlo, monospace',
  'Lucida Sans Unicode': '"Lucida Sans Unicode", "Lucida Grande", "Helvetica Neue", sans-serif',
  'Montserrat': 'Montserrat, Futura, "Avenir Next", sans-serif',
  'MS Sans Serif': '"MS Sans Serif", Geneva, Verdana, sans-serif',
  'Open Sans': '"Open Sans", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  'Palatino Linotype': '"Palatino Linotype", Palatino, "Book Antiqua", "Hoefler Text", serif',
  'Poppins': 'Poppins, Futura, "Century Gothic", "Avenir Next", sans-serif',
  'Roboto': 'Roboto, "Helvetica Neue", Arial, sans-serif',
  'Rockwell': 'Rockwell, "American Typewriter", Courier, "Noto Serif", serif',
  'Segoe Print': '"Segoe Print", "Bradley Hand", "Marker Felt", casual, cursive',
  'Segoe UI': '"Segoe UI", -apple-system, "Helvetica Neue", Roboto, sans-serif',
  'Sitka': 'Sitka, "Sitka Text", Charter, "Iowan Old Style", "Noto Serif", serif',
  'Tahoma': 'Tahoma, Geneva, Verdana, "Noto Sans", sans-serif',
  'Times New Roman': '"Times New Roman", Times, "Noto Serif", serif',
  'Trebuchet MS': '"Trebuchet MS", "Lucida Grande", Tahoma, "Noto Sans", sans-serif',
  'Verdana': 'Verdana, Geneva, Tahoma, "Noto Sans", sans-serif',
};

// customStyleMap for the <Editor>: react-draft-wysiwyg merges this OVER
// draftjs-utils' generated map, so every `fontfamily-X` the toolbar can produce
// renders with its full stack instead of the bare family name. Families we have
// no mapping for are left to draftjs-utils' default (bare name), exactly as
// before.
export const fontFamilyStyleMap = Object.fromEntries(
  Object.entries(FONT_FAMILY_STACKS).map(([family, stack]) => [
    `fontfamily-${family}`,
    { fontFamily: stack },
  ])
);

const unwrapListItemParagraphs = (doc) => {
  doc.querySelectorAll('li > p').forEach((paragraph) => {
    const parent = paragraph.parentNode;

    while (paragraph.firstChild) {
      parent.insertBefore(paragraph.firstChild, paragraph);
    }

    parent.removeChild(paragraph);
  });
};

const removeEmptyListSeparators = (doc) => {
  doc.querySelectorAll('ul > p, ol > p, ul > div, ol > div').forEach((node) => {
    if (!node.textContent.trim() && node.children.length === 0) {
      node.parentNode.removeChild(node);
    }
  });
};

const isBlankNode = (node) =>
  !node.textContent.trim() &&
  Array.from(node.childNodes).every(
    (child) => child.nodeName === 'BR' || !child.textContent.trim()
  );

const moveChildren = (source, target) => {
  while (source.firstChild) {
    target.appendChild(source.firstChild);
  }
};

const orderedMarkerPattern = /^\s*((?:\d+|[a-zA-Z]|[ivxlcdmIVXLCDM]+)[.)])\s*(.*)$/;
const unorderedMarkerPattern = /^\s*([•·●○▪▫*\\-])\s*(.*)$/;

const getListMarkerMatch = (text) => {
  const orderedMatch = text.match(orderedMarkerPattern);
  if (orderedMatch) {
    return {
      type: 'OL',
      marker: orderedMatch[1],
      text: orderedMatch[2].trim(),
    };
  }

  const unorderedMatch = text.match(unorderedMarkerPattern);
  if (unorderedMatch) {
    return {
      type: 'UL',
      marker: unorderedMatch[1],
      text: unorderedMatch[2].trim(),
    };
  }

  return null;
};

const replaceFakeListParagraphs = (doc) => {
  const children = Array.from(doc.body.children);
  let currentList = null;

  const endCurrentList = () => {
    currentList = null;
  };

  children.forEach((node) => {
    if (!node.parentNode || !['P', 'DIV'].includes(node.tagName)) {
      endCurrentList();
      return;
    }

    const markerMatch = getListMarkerMatch(node.textContent.replace(/\u00a0/g, ' '));
    if (!markerMatch) {
      endCurrentList();
      return;
    }

    const next = node.nextElementSibling;
    const markerOnly = markerMatch.text === '';
    const nextIsTextBlock =
      markerOnly &&
      next &&
      ['P', 'DIV'].includes(next.tagName) &&
      !isBlankNode(next) &&
      !getListMarkerMatch(next.textContent.replace(/\u00a0/g, ' '));

    const item = doc.createElement('li');
    item.textContent = nextIsTextBlock ? next.textContent.trim() : markerMatch.text;

    if (!currentList || currentList.tagName !== markerMatch.type) {
      currentList = doc.createElement(markerMatch.type.toLowerCase());
      node.parentNode.insertBefore(currentList, node);
    }

    currentList.appendChild(item);
    node.parentNode.removeChild(node);

    if (nextIsTextBlock) {
      next.parentNode.removeChild(next);
    }
  });
};

const repairEmptyListItemsBeforeTextBlocks = (doc) => {
  Array.from(doc.body.children).forEach((node) => {
    if (!['OL', 'UL'].includes(node.tagName)) return;

    const listItems = Array.from(node.children).filter((child) => child.tagName === 'LI');
    if (listItems.length !== 1 || !isBlankNode(listItems[0])) return;

    const next = node.nextElementSibling;
    if (!next || !['P', 'DIV'].includes(next.tagName) || isBlankNode(next)) return;

    listItems[0].textContent = '';
    moveChildren(next, listItems[0]);
    next.parentNode.removeChild(next);
  });
};

const mergeAdjacentLists = (doc) => {
  Array.from(doc.body.children).forEach((node) => {
    if (!['OL', 'UL'].includes(node.tagName)) return;

    let next = node.nextElementSibling;
    while (next && next.tagName === node.tagName) {
      const listToMerge = next;
      next = next.nextElementSibling;
      moveChildren(listToMerge, node);
      listToMerge.parentNode.removeChild(listToMerge);
    }
  });
};

const allowedTags = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'DEL',
  'DIV',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'I',
  'IMG',
  'INS',
  'LI',
  'OL',
  'P',
  'PRE',
  'SPAN',
  'STRIKE',
  'STRONG',
  'U',
  'UL',
  'VIDEO',
]);

const blockedTags = new Set([
  'IFRAME',
  'META',
  'NOSCRIPT',
  'OBJECT',
  'SCRIPT',
  'STYLE',
]);

const allowedAttributes = {
  A: new Set(['href', 'target', 'rel']),
  // style carries the image alignment (float) - the only form html-to-draftjs
  // can restore it from on reload.
  IMG: new Set(['src', 'alt', 'title', 'width', 'height', 'style']),
  VIDEO: new Set(['src', 'data-file-name']),
  SPAN: new Set(['style']),
  P: new Set(['style']),
  DIV: new Set(['style']),
  LI: new Set(['style']),
};

const allowedStyleProperties = new Set([
  'background-color',
  'color',
  'float',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'text-align',
  'text-decoration',
]);

// On paste we keep every formatting feature our own toolbar offers (bold /
// italic / underline / strike, alignment, text color, highlight, font family,
// font size) so an article keeps its look. Values are normalized to what the
// toolbar itself could have produced - sizes snap to the size dropdown's
// options, fonts must exist in the font dropdown (see
// normalizePastedStyleValue). Anything the toolbar can't produce
// (line-height, margins, arbitrary layout) is still dropped so pasted content
// behaves like native content afterwards.
const pasteAllowedStyleProperties = new Set([
  'background-color',
  'color',
  // float only has an effect on <img> (html-to-draftjs maps it to the image's
  // alignment), so an article's left/right floated images keep their position
  // and text keeps wrapping around them.
  'float',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'text-align',
  'text-decoration',
]);

// The library's default fontSize dropdown options - what our toolbar shows.
const TOOLBAR_FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

// White/transparent article backgrounds would paint invisible highlights over
// the white editor; drop them instead of storing useless style spans.
const uselessBackgroundPattern =
  /^(#fff(?:fff)?|white|transparent|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(?:,[^)]*)?\)|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0(?:\.0*)?\s*\)|inherit|initial|unset)$/i;

// Converts a pasted css font-size to px and snaps it to the nearest size our
// toolbar dropdown offers. Snapping also guarantees a unitless integer token,
// which is the only form draftjs-utils can register for rendering
// (extractInlineStyle does +substr - '17.6px' would become NaN and never show).
const snapFontSizeToToolbar = (value) => {
  const match = value.match(/^([\d.]+)\s*(px|pt|em|rem|%)?$/i);
  if (!match) return null;
  const number = parseFloat(match[1]);
  if (!Number.isFinite(number) || number <= 0) return null;

  const unit = (match[2] || 'px').toLowerCase();
  const px =
    unit === 'pt' ? number * (4 / 3)
      : unit === 'em' || unit === 'rem' ? number * 16
        : unit === '%' ? number * 0.16
          : number;

  return TOOLBAR_FONT_SIZES.reduce((best, size) =>
    Math.abs(size - px) < Math.abs(best - px) ? size : best
  );
};

// Maps a pasted style value onto what our toolbar could have produced itself.
// Returns null to drop the declaration entirely.
const normalizePastedStyleValue = (property, value, tagName) => {
  const trimmed = value.trim();

  switch (property) {
    case 'font-size': {
      const px = snapFontSizeToToolbar(trimmed);
      return px ? `${px}px` : null;
    }
    case 'font-family': {
      // Keep the first family of the stack, but only when our font dropdown
      // offers it (canonical casing so the dropdown highlights it).
      const first = trimmed.split(',')[0].trim().replace(/^['"]|['"]$/g, '');
      return (
        richTextEditorToolbar.fontFamily.options.find(
          (font) => font.toLowerCase() === first.toLowerCase()
        ) || null
      );
    }
    case 'font-weight': {
      // html-to-draftjs only understands the literal 'bold'; articles mostly
      // use numeric weights (600/700).
      if (/^(bold|bolder)$/i.test(trimmed)) return 'bold';
      const numeric = parseInt(trimmed, 10);
      return Number.isFinite(numeric) && numeric >= 600 ? 'bold' : null;
    }
    case 'background-color':
      // Only keep highlights that were applied to the text itself (span).
      // Backgrounds on divs/paragraphs/cells are page layout - the converter
      // would smear them over all the text inside as a fake highlight.
      if (tagName !== 'SPAN') return null;
      return uselessBackgroundPattern.test(trimmed) ? null : trimmed;
    case 'color':
      return /^(inherit|initial|unset|currentcolor)$/i.test(trimmed) ? null : trimmed;
    case 'float':
      // Only meaningful on images; 'none' would map to a bogus alignment.
      return /^(left|right)$/i.test(trimmed) ? trimmed.toLowerCase() : null;
    default:
      return trimmed;
  }
};

const isSafeUrl = (value) => {
  const trimmedValue = value.trim().toLowerCase();
  return (
    trimmedValue.startsWith('http://') ||
    trimmedValue.startsWith('https://') ||
    trimmedValue.startsWith('mailto:') ||
    trimmedValue.startsWith('tel:') ||
    // blob: = local image preview (deferred upload). It must survive sanitize so
    // flushUploads can find it in the html, upload it, and swap it for the /key.
    trimmedValue.startsWith('blob:') ||
    (trimmedValue.startsWith('data:image/') && !trimmedValue.startsWith('data:image/svg')) ||
    trimmedValue.startsWith('/')
  );
};

const sanitizeStyle = (styleValue, allowedProperties = allowedStyleProperties, paste = false, tagName = '') =>
  styleValue
    .split(';')
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const [property, ...valueParts] = rule.split(':');
      if (!property || valueParts.length === 0) return null;

      const propertyName = property.trim().toLowerCase();
      let propertyValue = valueParts.join(':').trim();

      if (!allowedProperties.has(propertyName)) return null;
      if (/expression|javascript:|url\s*\(/i.test(propertyValue)) return null;

      if (paste) {
        propertyValue = normalizePastedStyleValue(propertyName, propertyValue, tagName);
        if (!propertyValue) return null;
      } else if (propertyName === 'font-family' || propertyName === 'font-size') {
        const normalized = normalizePastedStyleValue(propertyName, propertyValue, tagName);
        if (!normalized) return null;
        propertyValue = normalized;
      }

      return `${propertyName}: ${propertyValue}`;
    })
    .filter(Boolean)
    .join('; ');

const unwrapNode = (node) => {
  const parent = node.parentNode;
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
};

const sanitizeNode = (node, options = {}) => {
  const { paste = false } = options;
  const styleProperties = paste ? pasteAllowedStyleProperties : allowedStyleProperties;

  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.COMMENT_NODE) {
      child.parentNode.removeChild(child);
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = child.tagName;
    if (blockedTags.has(tagName)) {
      child.parentNode.removeChild(child);
      return;
    }

    // Anchors used to be unwrapped on paste (text kept, link discarded):
    // replaceWithFragment orphaned their LINK entities, and convert-to-html
    // then threw "Unknown DraftEntity key" and took the SAVE down with it.
    // stripOrphanEntities (see getContentHtml) now clears dangling references
    // before every convertToRaw, so that crash is handled at the point it
    // actually occurs - and unwrapping here only silently destroyed every link
    // in pasted content. Anchors now fall through to the normal allowlist
    // below, which keeps href/target/rel, drops javascript: urls via
    // isSafeUrl, and forces rel="noopener noreferrer".

    if (paste && tagName === 'IMG') {
      // Keep remote images IN PLACE: html-to-draftjs turns each one into an
      // atomic block exactly where it sat in the pasted layout (and
      // hydratePastedImages later swaps fetchable srcs for local blob
      // previews). Unfetchable sources (data:, file:, relative) are dropped.
      const imgSrc = (child.getAttribute('src') || '').trim();
      if (!/^https?:\/\//i.test(imgSrc) && !/^data:image\//i.test(imgSrc)) {
        child.parentNode.removeChild(child);
        return;
      }
    }

    if (!allowedTags.has(tagName)) {
      unwrapNode(child);
      return;
    }

    Array.from(child.attributes).forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();
      const tagAllowedAttributes = allowedAttributes[tagName] || new Set();

      if (attributeName.startsWith('on') || !tagAllowedAttributes.has(attributeName)) {
        child.removeAttribute(attribute.name);
        return;
      }

      if ((attributeName === 'href' || attributeName === 'src') && !isSafeUrl(attribute.value)) {
        child.removeAttribute(attribute.name);
        return;
      }

      if (attributeName === 'style') {
        const safeStyle = sanitizeStyle(attribute.value, styleProperties, paste, tagName);
        if (safeStyle) {
          child.setAttribute('style', safeStyle);
        } else {
          child.removeAttribute('style');
        }
      }
    });

    if (tagName === 'A') {
      child.setAttribute('rel', 'noopener noreferrer');
    }

    sanitizeNode(child, options);
  });
};

export const normalizeEditorHtml = (html, options = {}) => {
  if (!html || typeof DOMParser === 'undefined') return html || '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  replaceFakeListParagraphs(doc);
  unwrapListItemParagraphs(doc);
  removeEmptyListSeparators(doc);
  repairEmptyListItemsBeforeTextBlocks(doc);
  mergeAdjacentLists(doc);
  sanitizeNode(doc.body, options);

  return doc.body.innerHTML;
};

export const createEditorStateFromHtml = (html) => {
  if (!html) return EditorState.createEmpty();

  return EditorState.createWithContent(createContentStateFromHtml(html));
};

const isListBlock = (block) =>
  block.type === 'ordered-list-item' || block.type === 'unordered-list-item';

const normalizeRawBlocks = (rawContent) => {
  const blocks = [];

  for (let index = 0; index < rawContent.blocks.length; index += 1) {
    const block = rawContent.blocks[index];
    const nextBlock = rawContent.blocks[index + 1];

    if (
      isListBlock(block) &&
      block.text.trim() === '' &&
      nextBlock &&
      nextBlock.type === 'unstyled' &&
      nextBlock.text.trim() !== ''
    ) {
      blocks.push({
        ...block,
        text: nextBlock.text,
        inlineStyleRanges: nextBlock.inlineStyleRanges,
        entityRanges: nextBlock.entityRanges,
      });
      index += 1;
      continue;
    }

    blocks.push(block);
  }

  return {
    ...rawContent,
    blocks,
  };
};

// Article html is full of structural wrappers that convert to empty blocks,
// leaving huge vertical gaps between the pasted paragraphs/images. Collapse
// runs of blank blocks to a single one and trim leading/trailing blanks.
// Paste only - a saved description is our own output and a user may have
// intentionally added blank lines.
const isBlankRawBlock = (block) =>
  block.type === 'unstyled' &&
  !block.text.trim() &&
  (!block.entityRanges || block.entityRanges.length === 0);

const collapseBlankRawBlocks = (rawContent) => {
  const blocks = [];

  rawContent.blocks.forEach((block) => {
    if (
      isBlankRawBlock(block) &&
      (blocks.length === 0 || isBlankRawBlock(blocks[blocks.length - 1]))
    ) {
      return;
    }
    blocks.push(block);
  });

  while (blocks.length > 0 && isBlankRawBlock(blocks[blocks.length - 1])) {
    blocks.pop();
  }

  return { ...rawContent, blocks: blocks.length > 0 ? blocks : rawContent.blocks.slice(0, 1) };
};

// Atomic (image/video) blocks are contentEditable=false, so a caret can only
// ever sit in a TEXT block. Pasted article html routinely produces an image as
// the first/last block, or two images back to back, which leaves nowhere to tap
// to get a cursor "under the image" and nothing for the space-bar cursor drag
// to travel through - the caret simply cannot cross the image. Guarantee a text
// block on both sides of every atomic block so those positions exist.
// Runs AFTER collapseBlankRawBlocks, which would otherwise strip the trailing
// spacer straight back off.
const emptyRawBlock = () => ({
  key: genKey(),
  text: '',
  type: 'unstyled',
  depth: 0,
  inlineStyleRanges: [],
  entityRanges: [],
  data: {},
});

const isAtomicRawBlock = (block) => block && block.type === 'atomic';

const ensureAtomicBlocksAreNavigable = (rawContent) => {
  const blocks = [];

  rawContent.blocks.forEach((block, index) => {
    const previous = rawContent.blocks[index - 1];

    if (isAtomicRawBlock(block) && (index === 0 || isAtomicRawBlock(previous))) {
      blocks.push(emptyRawBlock());
    }

    blocks.push(block);
  });

  if (isAtomicRawBlock(blocks[blocks.length - 1])) {
    blocks.push(emptyRawBlock());
  }

  return { ...rawContent, blocks };
};

const normalizeContentState = (contentState, { paste = false } = {}) => {
  let raw = normalizeRawBlocks(convertToRaw(contentState));
  if (paste) raw = collapseBlankRawBlocks(raw);
  raw = ensureAtomicBlocksAreNavigable(raw);
  return convertFromRaw(raw);
};

// html-to-draftjs's built-in <video> handling only reads src/height/width/
// float into the VIDEO entity (it has no notion of a video "alt" text), so
// the filename we serialize as data-file-name (see videoEntityTransform)
// would otherwise be lost on every reload. Restore it here by matching src.
const restoreVideoFileNames = (contentState, sanitizedHtml) => {
  if (typeof DOMParser === 'undefined') return contentState;

  const fileNameBySrc = {};
  new DOMParser()
    .parseFromString(sanitizedHtml, 'text/html')
    .querySelectorAll('video')
    .forEach((video) => {
      const src = video.getAttribute('src');
      const fileName = video.getAttribute('data-file-name');
      if (src && fileName) fileNameBySrc[src] = fileName;
    });

  if (Object.keys(fileNameBySrc).length === 0) return contentState;

  let updated = contentState;
  updated.getBlockMap().forEach((block) => {
    if (block.getType() !== 'atomic') return;
    const entityKey = block.getEntityAt(0);
    if (!entityKey) return;
    const entity = updated.getEntity(entityKey);
    if (entity.getType() !== 'VIDEO') return;

    const fileName = fileNameBySrc[entity.getData().src];
    if (fileName) {
      updated = updated.mergeEntityData(entityKey, { fileName });
    }
  });

  return updated;
};

const createContentStateFromHtml = (html, options = {}) => {
  const sanitizedHtml = normalizeEditorHtml(html, options);
  const { contentBlocks, entityMap } = htmlToDraft(sanitizedHtml);
  let contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  contentState = restoreVideoFileNames(contentState, sanitizedHtml);

  return normalizeContentState(contentState, options);
};

// Pasting/editing can leave characters referencing an entity key that is no
// longer in the content's entity map (draft-js orphans them during
// replaceWithFragment). convertToRaw then throws "Unknown DraftEntity key" and
// the SAVE fails. Strip any dangling entity reference so convert/save never
// crashes; the (still valid) text and real entities are kept.
const stripOrphanEntities = (contentState) => {
  let cleaned = contentState;

  contentState.getBlockMap().forEach((block) => {
    block.findEntityRanges(
      (character) => {
        const key = character.getEntity();
        if (!key) return false;
        try {
          contentState.getEntity(key);
          return false;
        } catch (error) {
          return true;
        }
      },
      (start, end) => {
        const selection = SelectionState.createEmpty(block.getKey()).merge({
          anchorOffset: start,
          focusOffset: end,
        });
        cleaned = Modifier.applyEntity(cleaned, selection, null);
      }
    );
  });

  return cleaned;
};

// draftjs-to-html saves image alignment as a <div style="text-align:..">
// wrapper, but html-to-draftjs only restores alignment from an inline
// float style on the <img> itself - so alignment silently died on every
// save/reload. Serialize IMAGE entities ourselves with the float style the
// loader actually understands (center keeps the text-align wrapper: a
// centered image is not floated).
const imageEntityTransform = (entity) => {
  if (entity.type !== 'IMAGE') return undefined;

  const { src = '', alt = '', alignment } = entity.data || {};
  const img = (style) =>
    `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"${style ? ` style="${style}"` : ''}/>`;

  if (alignment === 'left' || alignment === 'right') {
    return img(`float:${alignment};`);
  }
  if (alignment) {
    return `<div style="text-align:center;">${img('')}</div>`;
  }
  return img('');
};

// draftjs-to-html has no built-in VIDEO handling (unlike html-to-draftjs,
// which already turns a pasted/loaded <video> into a VIDEO entity on import) -
// serialize it ourselves as a plain <video src> so the round trip is symmetric.
const videoEntityTransform = (entity) => {
  if (entity.type !== 'VIDEO') return undefined;

  const { src = '', fileName = '' } = entity.data || {};
  return `<video src="${escapeHtml(src)}" data-file-name="${escapeHtml(fileName)}"></video>`;
};

const descriptionEntityTransform = (entity, text) =>
  imageEntityTransform(entity) || videoEntityTransform(entity);

// Final pass over the html we hand to the backend / the native apps: swap each
// bare `font-family: Calibri` for its full fallback stack (see
// FONT_FAMILY_STACKS). Without this the mobile task viewer - which renders the
// stored description html directly, outside this editor's customStyleMap - hits
// exactly the same "family doesn't exist on the device" wall the editor did.
//
// Runs AFTER normalizeEditorHtml on purpose: sanitizeStyle canonicalises
// font-family down to a single dropdown family, so by this point every value is
// a clean key we can look up. Loading the html back collapses the stack to its
// first family again, so the round trip and the dropdown selection are
// unaffected.
const expandFontFamilyStacks = (html) => {
  if (!html || typeof DOMParser === 'undefined') return html || '';
  if (html.indexOf('font-family') === -1) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('[style*="font-family"]').forEach((el) => {
    const family = el.style.fontFamily.trim().replace(/^['"]|['"]$/g, '');
    const stack = FONT_FAMILY_STACKS[family];
    if (stack) el.style.fontFamily = stack;
  });

  return doc.body.innerHTML;
};

export const getContentHtml = (contentState) =>
  expandFontFamilyStacks(
    normalizeEditorHtml(
      draftToHtml(
        convertToRaw(stripOrphanEntities(contentState)),
        undefined,
        undefined,
        descriptionEntityTransform
      )
    )
  );

export const getEditorHtml = (editorState) =>
  getContentHtml(editorState.getCurrentContent());

// ---------------------------------------------------------------------------
// Description image storage-key handling.
//
// Images pasted/uploaded into a description are stored on S3 as task attachments.
// The persisted description html keeps only the STABLE storage key (e.g.
// "/tasks/12/3/pic.png") as the <img src>, never the signed url (which expires).
// On display the key is swapped for a freshly-signed protected url.
// ---------------------------------------------------------------------------

const STORAGE_KEY_PATH = /^tasks\/\d+\//i;

// Given any <img src>, return its stable storage key (no leading slash) or null
// when the src is not a task-storage image (external url, data-uri, etc.).
export const extractStorageKey = (src) => {
  if (!src) return null;

  const trimmed = src.trim();
  if (trimmed.startsWith('/tasks/')) {
    return trimmed.replace(/^\/+/, '').split('?')[0];
  }

  if (/^https?:\/\//i.test(trimmed) && typeof window !== 'undefined') {
    try {
      const path = new URL(trimmed, window.location.origin).pathname.replace(/^\/+/, '');
      if (STORAGE_KEY_PATH.test(path)) return path;
    } catch (error) {
      return null;
    }
  }

  return null;
};

// Unique list of storage keys referenced by a description html string
// (inline images and pasted-video attachments alike - both are stored the
// same way, just under different original file extensions).
export const collectDescriptionImageKeys = (html) => {
  if (!html || typeof DOMParser === 'undefined') return [];

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const keys = [];

  doc.querySelectorAll('img, video').forEach((el) => {
    const key = extractStorageKey(el.getAttribute('src'));
    if (key) keys.push(key);
  });

  return Array.from(new Set(keys));
};

// Swap stored storage keys for freshly-signed urls for inline display.
// Returns the rewritten html plus a map (signedUrl -> stored "/key") so the
// editor can convert the signed urls back to keys when saving.
export const applyResolvedImageSrc = (html, urlByKey) => {
  if (!html || typeof DOMParser === 'undefined') return { html: html || '', keyBySrc: {} };

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const keyBySrc = {};

  doc.querySelectorAll('img, video').forEach((el) => {
    const key = extractStorageKey(el.getAttribute('src'));
    if (!key) return;

    const resolved = urlByKey[key];
    if (resolved) {
      el.setAttribute('src', resolved);
      keyBySrc[resolved] = `/${key}`;
    }
  });

  return { html: doc.body.innerHTML, keyBySrc };
};

// Removes the atomic block the cursor is currently inside (or adjacent to when
// `command` is 'delete'/'backspace'). Returns a new EditorState, or null when
// there is nothing to remove so the caller can fall through to the default handler.
export const removeAtomicBlock = (editorState, command) => {
  if (command !== 'backspace' && command !== 'delete') return null;

  const selection = editorState.getSelection();
  if (!selection.isCollapsed()) return null;

  const content = editorState.getCurrentContent();
  const blockKey = selection.getStartKey();
  const block = content.getBlockForKey(blockKey);

  // When the cursor is inside the atomic block itself.
  let atomicKey = block.getType() === 'atomic' ? blockKey : null;

  // When cursor is at the very start of the next block → backspace removes the preceding atomic.
  if (!atomicKey && command === 'backspace' && selection.getStartOffset() === 0) {
    const prev = content.getBlockBefore(blockKey);
    if (prev && prev.getType() === 'atomic') atomicKey = prev.getKey();
  }

  // When cursor is at the very end of the previous block → delete removes the following atomic.
  if (!atomicKey && command === 'delete' && selection.getStartOffset() === block.getLength()) {
    const next = content.getBlockAfter(blockKey);
    if (next && next.getType() === 'atomic') atomicKey = next.getKey();
  }

  if (!atomicKey) return null;

  const atomicBlock = content.getBlockForKey(atomicKey);
  const prevBlock = content.getBlockBefore(atomicKey);
  const nextBlock = content.getBlockAfter(atomicKey);

  let targetSelection;
  if (prevBlock) {
    targetSelection = selection.merge({
      anchorKey: prevBlock.getKey(),
      anchorOffset: prevBlock.getLength(),
      focusKey: atomicKey,
      focusOffset: atomicBlock.getLength(),
      isBackward: false,
    });
  } else if (nextBlock) {
    targetSelection = selection.merge({
      anchorKey: atomicKey,
      anchorOffset: 0,
      focusKey: nextBlock.getKey(),
      focusOffset: 0,
      isBackward: false,
    });
  } else {
    // Only block in the document — clear its content but keep the block.
    targetSelection = selection.merge({
      anchorKey: atomicKey,
      anchorOffset: 0,
      focusKey: atomicKey,
      focusOffset: atomicBlock.getLength(),
      isBackward: false,
    });
  }

  const newContent = Modifier.removeRange(content, targetSelection, 'backward');
  const newState = EditorState.push(editorState, newContent, 'remove-range');
  return EditorState.forceSelection(newState, newContent.getSelectionAfter());
};

// Same fallback picture chat uses for missing media.
export const IMAGE_FALLBACK_SRC = '/images/thumbs/no-picture-taking.png';

// Any description <img> whose source fails to load (deleted from S3, expired
// signed url, dead hotlink) is swapped for the app's standard fallback
// picture instead of the browser's broken-image icon. Display-only: the
// persisted html keeps the original src, so if the object becomes available
// again the real image shows. Two mechanisms because one alone misses cases:
// - a capture 'error' listener (error events do not bubble) for images that
//   fail while we are watching;
// - a MutationObserver sweep for images that ALREADY finished failing before
//   this attached, or that draft-js re-created on a re-render (a re-created
//   node with a cached failed src never fires another error event).
// Returns the detach function.
export const attachImageFallback = (container) => {
  if (!container) return () => { };

  const applyFallback = (img) => {
    if (!img.closest('.public-DraftEditor-content')) return;
    if ((img.getAttribute('src') || '').endsWith(IMAGE_FALLBACK_SRC)) return;
    img.src = IMAGE_FALLBACK_SRC;
  };

  const onError = (event) => {
    const img = event.target;
    if (img && img.tagName === 'IMG') applyFallback(img);
  };

  // complete && naturalWidth 0 = the browser gave up on this src.
  const sweep = () => {
    container
      .querySelectorAll('.public-DraftEditor-content img')
      .forEach((img) => {
        if (img.complete && img.naturalWidth === 0) applyFallback(img);
      });
  };

  container.addEventListener('error', onError, true);


  let scheduled = 0;
  const scheduleSweep = () => {
    if (scheduled) return;
    scheduled = requestAnimationFrame(() => {
      scheduled = 0;
      sweep();
    });
  };

  const containsImage = (node) =>
    node.nodeType === 1 &&
    (node.nodeName === 'IMG' || (node.querySelector && node.querySelector('img')));

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (containsImage(node)) {
          scheduleSweep();
          return;
        }
      }
    }
  });
  observer.observe(container, { childList: true, subtree: true });
  sweep();

  return () => {
    container.removeEventListener('error', onError, true);
    if (scheduled) cancelAnimationFrame(scheduled);
    observer.disconnect();
  };
};

export const ensureTrailingTextBlock = (editorState) => {
  const content = editorState.getCurrentContent();
  const last = content.getLastBlock();

  if (!last || last.getType() !== 'atomic') return editorState;

  const endOfAtomic = SelectionState.createEmpty(last.getKey()).merge({
    anchorOffset: last.getLength(),
    focusOffset: last.getLength(),
  });

  let newContent = Modifier.splitBlock(content, endOfAtomic);
  newContent = Modifier.setBlockType(newContent, newContent.getSelectionAfter(), 'unstyled');

  const newState = EditorState.push(editorState, newContent, 'split-block');

  return EditorState.forceSelection(newState, editorState.getSelection());
};

export const placeCaretAfterBlock = (editorState, blockKey) => {
  const content = editorState.getCurrentContent();
  const block = content.getBlockForKey(blockKey);
  if (!block || block.getType() !== 'atomic') return null;

  const next = content.getBlockAfter(blockKey);
  if (next && next.getType() !== 'atomic') {
    const selection = SelectionState.createEmpty(next.getKey()).merge({ hasFocus: true });
    return EditorState.forceSelection(editorState, selection);
  }

  const endOfAtomic = SelectionState.createEmpty(blockKey).merge({
    anchorOffset: block.getLength(),
    focusOffset: block.getLength(),
  });
  let newContent = Modifier.splitBlock(content, endOfAtomic);
  const caret = newContent.getSelectionAfter().merge({ hasFocus: true });
  newContent = Modifier.setBlockType(newContent, caret, 'unstyled');
  const newState = EditorState.push(editorState, newContent, 'split-block');
  return EditorState.forceSelection(newState, caret);
};

// Inserts an image as an atomic IMAGE block (the same shape react-draft-wysiwyg's
// image button produces, so pasted/uploaded images render identically).
// No default alignment = centered block on its own line (Jira-style). The
// L/C/R popup can still float it left/right, where the css in index.css makes
// text wrap around it.
export const insertImageBlock = (editorState, src) => {
  const contentState = editorState.getCurrentContent();
  const contentWithEntity = contentState.createEntity('IMAGE', 'MUTABLE', {
    src,
    height: 'auto',
    width: 'auto',
    alt: '',
  });
  const entityKey = contentWithEntity.getLastCreatedEntityKey();
  const stateWithEntity = EditorState.set(editorState, {
    currentContent: contentWithEntity,
  });

  return AtomicBlockUtils.insertAtomicBlock(stateWithEntity, entityKey, ' ');
};

// Inserts a video as an atomic VIDEO block, same shape/insertion as
// insertImageBlock. fileName drives the attachment-card label and is
// serialized as data-file-name (see videoEntityTransform / restoreVideoFileNames)
// since it isn't part of html-to-draftjs's own <video> parsing.
export const insertVideoBlock = (editorState, src, fileName = '') => {
  const contentState = editorState.getCurrentContent();
  const contentWithEntity = contentState.createEntity('VIDEO', 'MUTABLE', {
    src,
    fileName,
  });
  const entityKey = contentWithEntity.getLastCreatedEntityKey();
  const stateWithEntity = EditorState.set(editorState, {
    currentContent: contentWithEntity,
  });

  return AtomicBlockUtils.insertAtomicBlock(stateWithEntity, entityKey, ' ');
};

// Convert signed/absolute description image/video urls back to their stable
// storage key before persisting, so the saved html never contains an expiring url.
export const persistDescriptionImages = (html, keyBySrc = {}) => {
  if (!html || typeof DOMParser === 'undefined') return html || '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('img, video').forEach((el) => {
    const src = el.getAttribute('src');
    if (!src) return;

    let storageSrc = keyBySrc[src];
    if (!storageSrc) {
      const key = extractStorageKey(src);
      if (key) storageSrc = `/${key}`;
    }

    if (storageSrc) {
      el.setAttribute('src', storageSrc);
    }
  });

  return doc.body.innerHTML;
};

const escapeHtml = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getListHtmlFromText = (text) => {
  if (!text) return null;

  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return null;

  const items = [];
  for (let index = 0; index < lines.length; index += 1) {
    const markerMatch = getListMarkerMatch(lines[index]);
    if (!markerMatch) return null;

    let itemText = markerMatch.text;
    if (!itemText) {
      index += 1;
      if (index >= lines.length || getListMarkerMatch(lines[index])) return null;
      itemText = lines[index];
    }

    items.push({ type: markerMatch.type, text: itemText });
  }

  if (items.length === 0 || !items.every((item) => item.type === items[0].type)) {
    return null;
  }

  const tagName = items[0].type.toLowerCase();
  return `<${tagName}>${items
    .map((item) => `<li>${escapeHtml(item.text)}</li>`)
    .join('')}</${tagName}>`;
};
const FONT_STYLE_PREFIXES = ['fontfamily-', 'fontsize-'];

const collectSelectionStyles = (contentState, selection, prefix) => {
  const found = new Set();
  if (selection.isCollapsed()) return found;

  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  let block = contentState.getBlockForKey(startKey);
  let done = false;

  while (block && !done) {
    const key = block.getKey();
    const from = key === startKey ? selection.getStartOffset() : 0;
    const to = key === endKey ? selection.getEndOffset() : block.getLength();

    for (let i = from; i < to; i += 1) {
      block.getInlineStyleAt(i).forEach((style) => {
        if (style && style.startsWith(prefix)) found.add(style);
      });
    }

    done = key === endKey;
    block = done ? null : contentState.getBlockAfter(key);
  }

  return found;
};

export const dedupeFontStyles = (previousState, nextState) => {
  const selection = nextState.getSelection();
  if (selection.isCollapsed()) return nextState;

  let content = nextState.getCurrentContent();
  let changed = false;
  const currentInline = nextState.getCurrentInlineStyle();

  FONT_STYLE_PREFIXES.forEach((prefix) => {
    const current = collectSelectionStyles(content, selection, prefix);
    if (current.size < 2) return;
    const previous = collectSelectionStyles(
      previousState.getCurrentContent(),
      selection,
      prefix
    );

    // The style the user just picked is the one that wasn't there before.
    const added = [...current].filter((style) => !previous.has(style));

    const keep =
      added.length === 1
        ? added[0]
        : [...current].find((style) => currentInline.has(style)) || added[0];

    if (!keep) return;

    current.forEach((style) => {
      if (style === keep) return;
      content = Modifier.removeInlineStyle(content, selection, style);
      changed = true;
    });
  });

  if (!changed) return nextState;

  return EditorState.forceSelection(
    EditorState.push(nextState, content, 'change-inline-style'),
    selection
  );
};

// Inserts the clipboard's plain text and nothing else. The floor we fall back to
// when rich handling cannot cope - losing formatting beats losing the page.
const insertPlainText = (onChange, text, state) => {
  if (!text) return false;

  const newContent = Modifier.replaceText(
    state.getCurrentContent(),
    state.getSelection(),
    text
  );

  onChange(EditorState.push(state, newContent, 'insert-characters'));
  return true;
};

export const createPastedTextHandler = (onChange) => (text, html, state) => {
  // Draft calls this straight from the paste event, so anything thrown here
  // escapes into React's render path and unmounts the whole tree - the editor
  // went blank down to the root background and only a reload brought it back.
  // A long article from the web is exactly the input that trips the html
  // normalizer/parser, so failure has to degrade to plain text, never to a crash.
  try {
    return pasteRichText(onChange, text, html, state);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Rich paste failed, falling back to plain text', error);

    try {
      return insertPlainText(onChange, text, state);
    } catch {
      // Even the fallback failed: let draft-js do whatever it does by default
      // rather than taking the page down with us.
      return false;
    }
  }
};

const pasteRichText = (onChange, text, html, state) => {
  const listHtmlFromText = getListHtmlFromText(text);
  const pastedHtml = listHtmlFromText || normalizeEditorHtml(html, { paste: true });

  if (pastedHtml) {
    const pastedContent = createContentStateFromHtml(pastedHtml, { paste: true });

    if (pastedContent) {
      const newContent = Modifier.replaceWithFragment(
        state.getCurrentContent(),
        state.getSelection(),
        pastedContent.getBlockMap()
      );

      // forceSelection, not just push: push updates draft's own selection but
      // leaves the DOM selection alone, so the browser's post-paste selection -
      // which spans the whole inserted fragment - stayed painted. On Android
      // that renders the entire editor as one blue highlight hiding the text
      // that was just pasted. Forcing collapses it to the caret at the end.
      onChange(
        EditorState.forceSelection(
          EditorState.push(state, newContent, 'insert-fragment'),
          newContent.getSelectionAfter()
        )
      );
      return true;
    }
  }

  // Source had rich html but it normalized to nothing (e.g. only links/images).
  // Insert the plain text ourselves so draft-js' default paste can't re-create
  // the entities we just stripped.
  if (html && text) {
    return insertPlainText(onChange, text, state);
  }

  return false;
};

// http(s) image urls referenced by pasted html. The paste sanitizer keeps
// these <img> in place (each becomes an atomic block at its original position
// in the pasted layout); hydratePastedImages then upgrades the fetchable ones
// to local blob previews.
export const extractPastedImageSrcs = (html) => {
  if (!html || typeof DOMParser === 'undefined') return [];

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const srcs = Array.from(doc.querySelectorAll('img'))
    .map((img) => img.getAttribute('src'))
    .filter((src) => src && (/^https?:\/\//i.test(src) || /^data:image\//i.test(src)));

  return [...new Set(srcs)];
};

// All IMAGE/VIDEO entity src values currently present in the content - used to
// spot when a pasted-but-not-yet-saved image or video was deleted so its
// upload can be aborted.
export const collectImageEntitySrcs = (contentState) => {
  const srcs = new Set();

  contentState.getBlockMap().forEach((block) => {
    if (block.getType() !== 'atomic') return;
    const entityKey = block.getEntityAt(0);
    if (!entityKey) return;
    const entity = contentState.getEntity(entityKey);
    const type = entity.getType();
    if ((type === 'IMAGE' || type === 'VIDEO') && entity.getData().src) {
      srcs.add(entity.getData().src);
    }
  });

  return srcs;
};

// Swaps the src stored in IMAGE entities (all occurrences of fromSrc) for
// toSrc, without moving any block - the image stays exactly where it was
// pasted.
export const replaceImageEntitySrc = (editorState, fromSrc, toSrc) => {
  let content = editorState.getCurrentContent();
  let changed = false;

  content.getBlockMap().forEach((block) => {
    if (block.getType() !== 'atomic') return;
    const entityKey = block.getEntityAt(0);
    if (!entityKey) return;
    const entity = content.getEntity(entityKey);
    if (entity.getType() === 'IMAGE' && entity.getData().src === fromSrc) {
      content = content.mergeEntityData(entityKey, { src: toSrc });
      changed = true;
    }
  });

  if (!changed) return editorState;

  const newState = EditorState.push(editorState, content, 'apply-entity');
  return EditorState.forceSelection(newState, editorState.getSelection());
};

// Best-effort in-place upgrade of the images in freshly pasted content:
// - Same-origin / CORS-open images are fetched, handed to addLocalImage (so
//   they upload to S3 on save = real attachment) and their entity src is
//   swapped for the local blob preview. The image never moves from its
//   pasted position.
// - CORS-blocked / unfetchable images keep their remote url (display-only
//   hotlink), which is already in place from the paste itself.
// replaceSrc(fromSrc, toSrc) is supplied by the caller and applies
// replaceImageEntitySrc to the live editor state.
export const hydratePastedImages = async (html, addLocalImage, replaceSrc) => {
  const srcs = extractPastedImageSrcs(html);

  await Promise.all(
    srcs.map(async (src, index) => {
      try {
        // data: is same-document, so no cors mode (which would reject it).
        const isData = /^data:image\//i.test(src);
        const response = await fetch(src, isData ? undefined : { mode: 'cors' });
        if (!response.ok) return;

        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) return;

        const ext = (blob.type.split('/')[1] || 'png').split('+')[0];
        const file = new File([blob], `pasted-${Date.now()}-${index}.${ext}`, {
          type: blob.type,
        });
        replaceSrc(src, addLocalImage(file));
      } catch (error) {
        // CORS / network failure -> keep the remote url (display-only hotlink).
      }
    })
  );
};
